import { FC, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { MileageHistoryItemResponseDto } from '@/apis/rest/generated/types/mileageHistoryItemResponseDto'
import { TextLegacy } from '@/components/common'
import { NewCol } from '@/components/common/layouts/NewCol'
import { NewRow } from '@/components/common/layouts/NewRow'
import { Typo, TypoVariant } from '@/components/common/typography'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { DATE_DISPLAY_FORMAT, DATE_TIME_DISPLAY_FORMAT, date2string } from '@/utils/utilDate'
import { numberWithCommas } from '@/utils/utilNumber'

type ListItemUserMileageRecordProps = {
  mileage: MileageHistoryItemResponseDto
}

const ListItemUserMileageRecord: FC<ListItemUserMileageRecordProps> = ({ mileage }) => {
  const { color } = useTheme()
  const [isExpanded, setIsExpanded] = useState(false)

  const { createdAt, message, subMessage, addMileage, nearestExpireAt, details, isNearExpiry } = mileage

  const isAdded = addMileage > 0
  const hasDetails = details && details.length > 1 && mileage.addMileage > 0

  const handleToggle = () => {
    if (hasDetails) {
      setIsExpanded((prev) => !prev)
    }
  }

  return (
    <StyledContainer>
      <MainRow $hasDetails={hasDetails} onClick={handleToggle}>
        <NewRow justify="space-between" align="center" wrap={false} style={{ flexGrow: 1 }}>
          <NewCol>
            <UpdateDateRow>
              <TextLegacy size={{ xs: 12, md: 14 }} color="gray400">
                {date2string(createdAt, DATE_TIME_DISPLAY_FORMAT)}
              </TextLegacy>
            </UpdateDateRow>

            <NewRow>
              <TextLegacy>{message}</TextLegacy>
            </NewRow>

            {subMessage && (
              <NewRow>
                <TextLegacy size={{ xs: 12, md: 14 }} color="gray400">
                  {subMessage}
                </TextLegacy>
              </NewRow>
            )}

            {isAdded && nearestExpireAt && (
              <NewRow align="center" style={{ gap: '6px' }}>
                <div style={{ width: '135px', fontVariantNumeric: 'tabular-nums' }}>
                  <TextLegacy size={{ xs: 12, md: 14 }} color="gray400">
                    {`유효기간 : ${date2string(nearestExpireAt)}`}
                  </TextLegacy>
                </div>
                {isNearExpiry && <ExpireBadge>곧 소멸</ExpireBadge>}
              </NewRow>
            )}
          </NewCol>

          <NewCol>
            <NewRow justify="flex-end" align="center">
              <NewCol>
                <NewRow justify="flex-end">
                  <Typo variant={TypoVariant.Heading6Bold} color={isAdded ? color.blue500 : color.gray400}>
                    {`${isAdded ? '+' : ''}${numberWithCommas(addMileage)}`}
                  </Typo>
                </NewRow>
              </NewCol>

              <IconWrapper $isExpanded={isExpanded} style={{ visibility: hasDetails ? 'visible' : 'hidden' }}>
                <svg viewBox="0 0 24 24" fill="currentColor" role="img">
                  <path
                    fillRule="nonzero"
                    clipRule="evenodd"
                    d="M11.2929 15.7071C11.6834 16.0976 12.3166 16.0976 12.7071 15.7071L18.7071 9.70711C19.0976 9.31658 19.0976 8.68342 18.7071 8.29289C18.3166 7.90237 17.6834 7.90237 17.2929 8.29289L12 13.5858L6.70711 8.29289C6.31658 7.90237 5.68342 7.90237 5.29289 8.29289C4.90237 8.68342 4.90237 9.31658 5.29289 9.70711L11.2929 15.7071Z"
                  />
                </svg>
              </IconWrapper>
            </NewRow>
          </NewCol>
        </NewRow>
      </MainRow>

      {hasDetails && isExpanded && (
        <DetailsWrapper>
          {details.map((detail, index) => {
            const isDetailAdded = detail.amount > 0

            return (
              <DetailRow key={index}>
                <NewRow align="center" style={{ gap: '8px' }}>
                  <BulletDot />

                  <div style={{ width: '150px' }}>
                    <Typo variant={TypoVariant.Body4Regular} color={color.gray500}>
                      유효기간: {date2string(detail.expireAt, DATE_DISPLAY_FORMAT)}
                    </Typo>
                  </div>

                  {detail.isNearExpiry && <ExpireBadge>곧 소멸</ExpireBadge>}
                </NewRow>

                <NewRow justify="flex-end" style={{ paddingRight: '3.6rem' }}>
                  <Typo variant={TypoVariant.Body4Bold} color={isDetailAdded ? color.blue500 : color.gray400}>
                    {`${isDetailAdded ? '+' : ''}${numberWithCommas(detail.amount)}`}
                  </Typo>
                </NewRow>
              </DetailRow>
            )
          })}
        </DetailsWrapper>
      )}
    </StyledContainer>
  )
}

const StyledContainer = styled.div`
  border-bottom: 1px solid ${GlobalColorEnum.Gray70};
`

const MainRow = styled.div<{ $hasDetails: boolean }>`
  display: flex;
  padding: 20px 30px 20px 20px;
  cursor: ${({ $hasDetails }) => ($hasDetails ? 'pointer' : 'default')};

  &:active {
    background-color: ${({ $hasDetails }) => ($hasDetails ? GlobalColorEnum.Gray30 : 'transparent')};
  }
`

const UpdateDateRow = styled(NewRow)`
  margin-bottom: 5px;
`

const IconWrapper = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  width: 2.4rem;
  height: 2.4rem;
  margin-left: 1.2rem;
  color: ${GlobalColorEnum.Gray400};
  transition: transform 0.2s ease-in-out;
  transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};

  svg {
    width: 100%;
    height: 100%;
  }
`

const DetailsWrapper = styled.div`
  padding: 16px 30px 20px 24px;
  background-color: ${GlobalColorEnum.Gray100};
  border-top: 1px dashed ${GlobalColorEnum.Gray70};
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const BulletDot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${GlobalColorEnum.Gray400};
`

const ExpireBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  border-radius: 4px;
  background-color: ${GlobalColorEnum.Red100};
  color: ${GlobalColorEnum.Red500};
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.2;
`

export default ListItemUserMileageRecord
